/**
 * Auth Context
 *
 * Firebase Auth + multi-role support.
 * Modeled after OnMove-Admin's battle-tested auth flow.
 *
 * Features:
 *  - useReducer + localStorage sync (survives page refresh)
 *  - Stable currentUserRef for token access without re-renders
 *  - Duplicate-fetch guard (lastUidRef + hasInitializedRef)
 *  - Suspended user detection
 *  - Auto-registration when no Firestore user doc exists
 *  - Proactive token refresh at 50-min intervals
 *  - Multi-role with backward compat (roles array + legacy role string)
 *  - activeRole priority: Firestore > localStorage > defaultRole > first role
 */

import { createContext, useReducer, useContext, useState, useEffect, useRef, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import logger from "services/logger";
import { endpoints } from "config/api";

// Firebase config from env
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------
function safeJsonParse(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Initial state (hydrated from localStorage)
// ---------------------------------------------------------------------------
const initialState = {
  userUid: localStorage.getItem("userUid") || null,
  roles: safeJsonParse("roles", []),
  activeRole: safeJsonParse("activeRole", null),
  defaultRole: safeJsonParse("defaultRole", null),
  roleAccess: safeJsonParse("roleAccess", {}),
  access: safeJsonParse("access", null),
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
function authReducer(state, action) {
  switch (action.type) {
    case "LOGIN":
      return { ...state, userUid: action.payload };

    case "LOGOUT":
      localStorage.removeItem("userUid");
      localStorage.removeItem("roles");
      localStorage.removeItem("activeRole");
      localStorage.removeItem("defaultRole");
      localStorage.removeItem("roleAccess");
      localStorage.removeItem("access");
      return {
        ...state,
        userUid: null,
        roles: [],
        activeRole: null,
        defaultRole: null,
        roleAccess: {},
        access: null,
      };

    case "SET_ROLES":
      return { ...state, roles: action.payload };

    case "SET_ACTIVE_ROLE": {
      const newActiveRole = action.payload;
      const newAccess = state.roleAccess[newActiveRole] || null;
      return {
        ...state,
        activeRole: newActiveRole,
        access: newAccess,
      };
    }

    case "SET_DEFAULT_ROLE":
      return { ...state, defaultRole: action.payload };

    case "SET_ROLE_ACCESS":
      return { ...state, roleAccess: action.payload };

    case "SET_ACCESS":
      return { ...state, access: action.payload };

    default:
      return state;
  }
}

export const AuthContext = createContext(null);

export function AuthContextProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Stable ref for token access without re-renders
  const currentUserRef = useRef(null);
  // Guards against duplicate fetches on token refresh
  const hasInitializedRef = useRef(false);
  const lastUidRef = useRef(null);

  // ---------------------------------------------------------------------------
  // localStorage sync
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (state.userUid) localStorage.setItem("userUid", state.userUid);
    else localStorage.removeItem("userUid");
  }, [state.userUid]);

  useEffect(() => { localStorage.setItem("roles", JSON.stringify(state.roles)); }, [state.roles]);
  useEffect(() => { localStorage.setItem("activeRole", JSON.stringify(state.activeRole)); }, [state.activeRole]);
  useEffect(() => { localStorage.setItem("defaultRole", JSON.stringify(state.defaultRole)); }, [state.defaultRole]);
  useEffect(() => { localStorage.setItem("roleAccess", JSON.stringify(state.roleAccess)); }, [state.roleAccess]);
  useEffect(() => { localStorage.setItem("access", JSON.stringify(state.access)); }, [state.access]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  const role = state.activeRole;
  const activeRole = state.activeRole;
  const roles = state.roles;
  const roleAccess = state.roleAccess;
  const adminAccess = state.roleAccess[state.activeRole] || null;

  // ---------------------------------------------------------------------------
  // getIdToken — stable, no re-renders
  // ---------------------------------------------------------------------------
  const getIdToken = useCallback(async (forceRefresh = false) => {
    const user = currentUserRef.current || auth.currentUser;
    if (!user) return null;
    return user.getIdToken(forceRefresh);
  }, []);

  // ---------------------------------------------------------------------------
  // fetchUserData — fetch Firestore doc, auto-register if missing
  // ---------------------------------------------------------------------------
  const fetchUserData = useCallback(async (uid) => {
    try {
      const token = await auth.currentUser.getIdToken(true);
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

      // --- Fetch user via backend API ---
      let resp = await fetch(endpoints.users.me, { headers });

      // --- Auto-register if user not found ---
      if (resp.status === 404) {
        logger.info("[Auth]", "No user doc found, auto-registering via backend");
        try {
          const regResp = await fetch(endpoints.auth.register, {
            method: "POST",
            headers,
            body: JSON.stringify({ displayName: auth.currentUser.displayName }),
          });
          if (!regResp.ok) {
            logger.error("[Auth]", "Auto-registration API failed:", regResp.status);
            dispatch({ type: "LOGOUT" });
            await signOut(auth);
            return null;
          }
          // Re-fetch after registration
          resp = await fetch(endpoints.users.me, { headers });
          if (!resp.ok) {
            logger.error("[Auth]", "User doc still missing after registration");
            dispatch({ type: "LOGOUT" });
            await signOut(auth);
            return null;
          }
        } catch (regErr) {
          logger.error("[Auth]", "Auto-registration error:", regErr.message);
          dispatch({ type: "LOGOUT" });
          await signOut(auth);
          return null;
        }
      }

      // --- Check for suspended (403) or other errors ---
      if (resp.status === 403) {
        logger.warn("[Auth]", "User account is suspended");
        dispatch({ type: "LOGOUT" });
        await signOut(auth);
        return null;
      }

      if (!resp.ok) {
        logger.error("[Auth]", "Failed to fetch user data:", resp.status);
        return null;
      }

      const { user: data } = await resp.json();

      setUserData(data);

      // --- Multi-role detection (with backward compat) ---
      let userRoles = [];
      let userDefaultRole = null;
      let userRoleAccess = {};

      if (data.roles && Array.isArray(data.roles) && data.roles.length > 0) {
        userRoles = data.roles;
        userDefaultRole = data.defaultRole || data.roles[0];
        userRoleAccess = data.roleAccess || {};
        if (Object.keys(userRoleAccess).length === 0) {
          userRoles.forEach((r) => { userRoleAccess[r] = data.access || "standard"; });
        }
      } else if (data.role) {
        userRoles = [data.role];
        userDefaultRole = data.role;
        userRoleAccess = { [data.role]: data.access || "standard" };
      } else {
        userRoles = ["user"];
        userDefaultRole = "user";
        userRoleAccess = { user: "standard" };
      }

      dispatch({ type: "SET_ROLES", payload: userRoles });
      dispatch({ type: "SET_DEFAULT_ROLE", payload: userDefaultRole });
      dispatch({ type: "SET_ROLE_ACCESS", payload: userRoleAccess });

      // --- Determine active role ---
      const serverActiveRole = data.activeRole;
      const storedActiveRole = safeJsonParse("activeRole", null);

      const effectiveActiveRole =
        (serverActiveRole && userRoles.includes(serverActiveRole))
          ? serverActiveRole
          : (storedActiveRole && userRoles.includes(storedActiveRole))
            ? storedActiveRole
            : userDefaultRole;

      dispatch({ type: "SET_ACTIVE_ROLE", payload: effectiveActiveRole });

      // Sync activeRole to backend if it differs
      if (serverActiveRole !== effectiveActiveRole) {
        try {
          await fetch(endpoints.users.me, {
            method: "PATCH",
            headers,
            body: JSON.stringify({ activeRole: effectiveActiveRole }),
          });
          logger.debug("[Auth]", `Synced activeRole to backend: ${effectiveActiveRole}`);
        } catch (err) {
          logger.warn("[Auth]", "Could not sync activeRole:", err.message);
        }
      }

      // Update lastConnection via backend
      try {
        await fetch(endpoints.users.me, {
          method: "PATCH",
          headers,
          body: JSON.stringify({ lastConnection: true }),
        });
      } catch (err) {
        logger.warn("[Auth]", "Could not update lastConnection:", err.message);
      }

      return data;
    } catch (err) {
      logger.error("[Auth]", "Error fetching user data:", err.message);
      return null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Auth state listener — guarded against duplicate fetches
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        currentUserRef.current = user;

        const isNewUser = lastUidRef.current !== user.uid;
        const isInitialLoad = !hasInitializedRef.current;

        if (isNewUser || isInitialLoad) {
          lastUidRef.current = user.uid;
          hasInitializedRef.current = true;

          setCurrentUser(user);
          dispatch({ type: "LOGIN", payload: user.uid });
          await fetchUserData(user.uid);
        }
        // Same user (token refresh) — only update ref, no state change
        setAuthLoading(false);
      } else {
        lastUidRef.current = null;
        hasInitializedRef.current = false;
        currentUserRef.current = null;
        setCurrentUser(null);
        setUserData(null);
        setAuthLoading(false);
        dispatch({ type: "LOGOUT" });
      }
    });

    return () => unsubscribe();
  }, [fetchUserData]);

  // ---------------------------------------------------------------------------
  // Proactive token refresh — 50 min, silent, no state updates
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const refreshToken = async () => {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
          logger.debug("[Auth]", "Token refreshed silently");
        } catch (error) {
          const errorCode = error?.code || "";
          if (errorCode.includes("token-expired") || errorCode.includes("user-token-expired")) {
            logger.warn("[Auth]", "Token expired, signing out");
            dispatch({ type: "LOGOUT" });
            signOut(auth).catch(() => {});
          } else {
            logger.error("[Auth]", "Token refresh failed:", error.message);
          }
        }
      }
    };
    const intervalId = setInterval(refreshToken, 50 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // ---------------------------------------------------------------------------
  // switchRole
  // ---------------------------------------------------------------------------
  const switchRole = useCallback(async (newRole) => {
    if (!state.roles.includes(newRole)) {
      logger.warn("[Auth]", `Cannot switch to role "${newRole}" — not in user roles`);
      return false;
    }
    dispatch({ type: "SET_ACTIVE_ROLE", payload: newRole });
    if (state.userUid) {
      try {
        const token = await getIdToken();
        await fetch(endpoints.users.me, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ activeRole: newRole }),
        });
        logger.debug("[Auth]", `Persisted active role to backend: ${newRole}`);
      } catch (err) {
        logger.warn("[Auth]", "Could not persist role switch:", err.message);
      }
    }
    return true;
  }, [state.roles, state.userUid, getIdToken]);

  const hasRole = useCallback((r) => state.roles.includes(r), [state.roles]);
  const hasMultipleRoles = state.roles.length > 1;

  // ---------------------------------------------------------------------------
  // Auth actions
  // ---------------------------------------------------------------------------
  const login = useCallback(async (email, password) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result.user;
  }, []);

  const register = useCallback(async (email, password, displayName = null) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    const token = await credential.user.getIdToken();
    await fetch(endpoints.auth.register, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ displayName }),
    });
    return credential.user;
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const resetPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  const value = {
    // Loading
    authLoading,
    // Firebase user (reactive — use for login/logout detection only)
    currentUser,
    // Firestore user data
    userData,
    // Stable token access (use for API calls)
    getIdToken,
    // UID
    userUid: state.userUid,
    // Multi-role
    role,
    roles,
    activeRole,
    defaultRole: state.defaultRole,
    roleAccess,
    adminAccess,
    access: state.access,
    // Helpers
    hasRole,
    hasMultipleRoles,
    switchRole,
    // Auth actions
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    fetchUserData,
    // For components that need to dispatch directly
    dispatch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthContextProvider");
  return context;
}

export default AuthContext;
